"use client";

/**
 * PaidApiQuickstartCard — public-facing quickstart for the live x402 paid APIs.
 *
 * Purpose: any human or agent that lands on this origin immediately sees WHAT is
 * for sale, WHAT it costs, and HOW to make a paid call (30-second path: unpaid
 * request → 402 challenge → sign EIP-3009 → retry with payment). All facts are
 * static mirrors of the env-backed config in src/lib/x402-server.ts; the live 402
 * challenge remains authoritative (stated on the card).
 *
 * Honesty contract: states verifiable facts only. The earnings line is hard-coded
 * honest ("first external settlement pending") because this card is a SALES
 * surface, not a ledger — the ledger is earnings.json via /api/mission.
 */
import { useCallback, useState } from "react";
import { Check, Copy, ExternalLink, TerminalSquare } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const SKUS = [
  {
    route: "POST /api/x402/research-brief",
    name: "Research Brief",
    price: "$0.05",
    desc: "Live web search + reads 3-6 sources, returns a cited brief (summary, key findings, confidence).",
    input: '{"topic":"…10-200 chars","focus?":"…","max_sources?":3-6}',
  },
  {
    route: "POST /api/x402/quick-brief",
    name: "Quick Brief",
    price: "$0.01",
    desc: "One live web search, snippet-level digest with 3-6 cited key points in seconds.",
    input: '{"topic":"…10-200 chars","recency_days?":0|7|30|90|365}',
  },
  {
    route: "GET /api/x402/wallet-balance",
    name: "Wallet Balance",
    price: "$0.001",
    desc: "Live native-asset balance on BTC/ETH/Base/SOL/TRON/RON via public RPCs.",
    input: "?chain=sol&address=<addr>",
  },
] as const;

const PAYTO = "0x40560Fa6E0908932Ab8F7bf8B2AFA18533e044Cc";
const NETWORK = "eip155:8453 · Base mainnet · USDC";

export function PaidApiQuickstartCard() {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = useCallback(async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      window.setTimeout(() => setCopied((k) => (k === key ? null : k)), 1600);
    } catch {
      /* clipboard unavailable — the text is still visible for manual copy */
    }
  }, []);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const curl = `curl -i "${origin}/api/x402/wallet-balance?chain=eth&address=${PAYTO}"`;

  return (
    <Card className="min-w-0 border-emerald-500/25 bg-zinc-900/40 shadow-none">
      <CardHeader className="min-w-0 pb-3">
        <CardTitle className="flex min-w-0 flex-wrap items-center justify-between gap-2 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">
          <span className="flex min-w-0 items-center gap-2">
            <TerminalSquare className="size-3.5 shrink-0 text-emerald-400" aria-hidden="true" />
            Paid APIs · x402 · quickstart
          </span>
          <span className="flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/10 font-mono text-[10px] text-emerald-400">
              no key · no signup
            </Badge>
            <Badge variant="outline" className="border-zinc-700 bg-zinc-800/40 font-mono text-[10px] text-zinc-500">
              {NETWORK}
            </Badge>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="min-w-0 space-y-4">
        {/* SKU table */}
        <div className="grid gap-2.5">
          {SKUS.map((s) => (
            <div
              key={s.route}
              className="flex min-w-0 flex-wrap items-start justify-between gap-x-3 gap-y-1 rounded-lg border border-zinc-800/70 bg-zinc-950/40 px-3 py-2.5"
            >
              <div className="min-w-0 flex-1 basis-64">
                <p className="flex flex-wrap items-baseline gap-2">
                  <span className="font-mono text-xs font-bold text-zinc-100">{s.name}</span>
                  <span className="font-mono text-[10px] text-zinc-500">{s.route}</span>
                </p>
                <p className="mt-0.5 text-[11px] leading-relaxed text-zinc-400">{s.desc}</p>
                <code className="mt-1 block overflow-x-auto whitespace-nowrap rounded bg-zinc-900/70 px-1.5 py-0.5 font-mono text-[10px] text-zinc-500">
                  {s.input}
                </code>
              </div>
              <span className="shrink-0 font-mono text-sm font-bold text-emerald-400">
                {s.price}
                <span className="text-[10px] font-normal text-zinc-500">/call</span>
              </span>
            </div>
          ))}
        </div>

        {/* 30-second path */}
        <div className="rounded-lg border border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.06] to-transparent p-3.5">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-400/90">
            30-second path to a paid call
          </p>
          <ol className="mt-2 list-inside list-decimal space-y-1 text-[11px] leading-relaxed text-zinc-300">
            <li>
              Send any request without payment → get the{" "}
              <code className="rounded bg-zinc-900/70 px-1 font-mono text-[10px] text-zinc-400">402 Payment-Required</code>{" "}
              challenge (base64 JSON: network, asset, amount, payTo).
            </li>
            <li>Sign the EIP-3009 transfer per the challenge — any x402 v2 client SDK does this for you.</li>
            <li>Retry the identical request with the payment attached → paid result. Failures (4xx/5xx) are never charged.</li>
          </ol>
          <div className="mt-3 flex min-w-0 flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 basis-64 overflow-x-auto whitespace-nowrap rounded-md border border-zinc-800 bg-zinc-950/70 px-2.5 py-1.5 font-mono text-[10px] text-zinc-300">
              {curl}
            </code>
            <Button
              size="sm"
              variant="outline"
              className="h-7 shrink-0 gap-1.5 border-zinc-700 bg-zinc-800/40 font-mono text-[10px] text-zinc-300 hover:bg-zinc-800"
              onClick={() => copy("curl", curl)}
              aria-label="Copy example curl command"
            >
              {copied === "curl" ? <Check className="size-3 text-emerald-400" /> : <Copy className="size-3" />}
              {copied === "curl" ? "copied" : "copy curl"}
            </Button>
          </div>
          <div className="mt-2.5 flex min-w-0 flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] text-zinc-500">Full catalog for agents:</span>
            <a
              href="/llms.txt"
              className="inline-flex items-center gap-1 font-mono text-[10px] text-emerald-400 underline-offset-2 hover:underline"
            >
              /llms.txt
              <ExternalLink className="size-3" aria-hidden="true" />
            </a>
            <span className="text-zinc-700">·</span>
            <button
              type="button"
              onClick={() => copy("payto", PAYTO)}
              className="inline-flex items-center gap-1 font-mono text-[10px] text-zinc-400 underline-offset-2 hover:text-zinc-200 hover:underline"
              title="Copy recipient address"
            >
              payTo {PAYTO.slice(0, 10)}…{PAYTO.slice(-6)}
              {copied === "payto" ? <Check className="size-3 text-emerald-400" /> : <Copy className="size-3 opacity-60" />}
            </button>
          </div>
        </div>

        <p className="text-[10px] leading-relaxed text-zinc-500">
          The live 402 challenge is authoritative for network/asset/payTo/price. Verified earnings to date: $0.00 —
          this ledger fills only with on-chain settlement evidence (see the Earnings tab); no buyer has paid yet.
        </p>
      </CardContent>
    </Card>
  );
}
